import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  bulkSendEndpoints,
  type BulkSendItemDto,
  type BulkSendJobDto,
  type BulkSendJobExecutionDto,
  type BulkSendJobsParams,
  type BulkSendRequestDto,
  type BulkSendSummaryDto,
  type ExclusionSummaryDto,
} from './endpoints';
import { buildFilterRequest, type BulkSendPatientFilter } from './filterRequest';

/** Poll cadence while any listed job is still running (or in the post-launch grace window). */
const JOBS_POLL_MS = 4000;

/** Steady poll cadence when the user turns on "keep updated" (auto-refresh), for delivery updates. */
const AUTO_REFRESH_MS = 10000;

const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['COMPLETED', 'FAILED', 'STOPPED', 'ABANDONED']);

function isTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

/** Inputs shared by preview + launch: the composed patient filter, template, and optional expiry. */
export interface BulkSendComposeInput {
  readonly filter: BulkSendPatientFilter;
  readonly templateId: string;
  readonly expiresAt: string;
}

export interface ExcludedRecipientViewModel {
  readonly personGuid: string;
  readonly reason: string;
}

export interface ExclusionSummaryViewModel {
  readonly sendable: number;
  readonly excluded: number;
  readonly byReason: readonly { readonly reason: string; readonly count: number }[];
  readonly excludedPatients: readonly ExcludedRecipientViewModel[];
}

export interface BulkSendItemViewModel {
  readonly entityType: string;
  readonly entityGuid: string;
  readonly recipient: string;
  readonly status: string;
  readonly errorCode: string;
  readonly errorMessage: string;
}

export interface BulkSendSummaryViewModel {
  readonly jobExecutionId: number;
  readonly total: number;
  readonly counts: readonly { readonly status: string; readonly count: number }[];
  readonly items: readonly BulkSendItemViewModel[];
}

function mapExclusionSummary(dto: ExclusionSummaryDto): ExclusionSummaryViewModel {
  return {
    sendable: dto.sendable ?? 0,
    excluded: dto.excluded ?? 0,
    byReason: Object.entries(dto.by_reason ?? {}).map(([reason, count]) => ({ reason, count })),
    excludedPatients: (dto.excluded_patients ?? []).map((e) => ({
      personGuid: e.person_guid ?? '',
      reason: e.reason ?? '',
    })),
  };
}

function mapItem(dto: BulkSendItemDto): BulkSendItemViewModel {
  return {
    entityType: dto.entity_type ?? '',
    entityGuid: dto.entity_guid ?? '',
    recipient: dto.recipient ?? '',
    status: dto.status ?? '',
    errorCode: dto.error_code ?? '',
    errorMessage: dto.error_message ?? '',
  };
}

function mapSummary(dto: BulkSendSummaryDto): BulkSendSummaryViewModel {
  return {
    jobExecutionId: dto.job_execution_id ?? 0,
    total: dto.total ?? 0,
    counts: Object.entries(dto.counts ?? {}).map(([status, count]) => ({ status, count })),
    items: (dto.items ?? []).map(mapItem),
  };
}

function toBody(input: BulkSendComposeInput): BulkSendRequestDto {
  const trimmedExpiry = input.expiresAt.trim();
  return {
    template_id: input.templateId,
    query: buildFilterRequest(input.filter),
    ...(trimmedExpiry.length > 0 ? { expires_at: trimmedExpiry } : {}),
    // Persisted verbatim on the job snapshot and rendered back on row-expand (display only).
    filter_criteria: JSON.stringify(input.filter),
  };
}

/** Preview the sendable/excluded split for the composed filter (Review step). Explicit trigger → mutation. */
export function useBulkSendPreview(): UseMutationResult<ExclusionSummaryViewModel, Error, BulkSendComposeInput> {
  return useMutation<ExclusionSummaryViewModel, Error, BulkSendComposeInput>({
    mutationFn: async (input) => mapExclusionSummary(await bulkSendEndpoints.preview(toBody(input))),
  });
}

/** Launch the bulk-send job. Fires the standard success toast; the jobs tables poll it in. */
export function useBulkSendLaunch(): UseMutationResult<BulkSendJobExecutionDto, Error, BulkSendComposeInput> {
  const queryClient = useQueryClient();
  return useMutation<BulkSendJobExecutionDto, Error, BulkSendComposeInput>({
    mutationFn: (input) => bulkSendEndpoints.launch(toBody(input)),
    meta: { successMessage: 'success.toast.title.bulk_send_started' },
    onSuccess: () => {
      // A bulk send creates questionnaire requests across many patients; refresh the cross-patient
      // Browse Questionnaires table. (The jobs table itself is refreshed by the panel's grace-poll.)
      queryClient.invalidateQueries({ queryKey: ['questionnaires', 'browse'] }).catch(() => undefined);
    },
  });
}

export interface BulkSendJobViewModel {
  readonly jobId: string;
  readonly jobName: string;
  readonly sendMethod: string;
  readonly templateId: string;
  readonly templateName: string | null;
  /** Launching user's guid; resolved to a display name in the view. */
  readonly createdBy: string | null;
  readonly expiresAt: string | null;
  /** Opaque wizard-filter JSON captured at launch (decoded for display on expand). */
  readonly filterCriteria: string | null;
  readonly createdOn: string | null;
  readonly status: string;
  readonly startTime: string | null;
  readonly endTime: string | null;
  readonly isTerminal: boolean;
  /** Delivery-attempt counts by status (e.g. {SENT:3, DELIVERED:2}); empty when none yet. */
  readonly counts: Readonly<Record<string, number>>;
}

export interface BulkSendJobPageViewModel {
  readonly rows: readonly BulkSendJobViewModel[];
  readonly totalElements: number;
  readonly totalPages: number;
  readonly page: number;
}

export interface BulkSendJobsFilters {
  readonly jobId: string;
  readonly createdBy: string | null;
  readonly templateId: string | null;
  readonly status: string | null;
}

function mapJob(dto: BulkSendJobDto): BulkSendJobViewModel {
  const status = dto.status ?? '';
  return {
    jobId: dto.job_execution_id === undefined ? '' : String(dto.job_execution_id),
    jobName: dto.job_name ?? '',
    sendMethod: dto.send_method ?? '',
    templateId: dto.template_id ?? '',
    templateName: dto.template_name ?? null,
    createdBy: dto.created_by ?? null,
    expiresAt: dto.expires_at ?? null,
    filterCriteria: dto.filter_criteria ?? null,
    createdOn: dto.created_on ?? null,
    status,
    startTime: dto.start_time ?? null,
    endTime: dto.end_time ?? null,
    isTerminal: isTerminal(status),
    counts: dto.counts ?? {},
  };
}

/**
 * Server-paginated + filtered bulk-send jobs. Polls while any listed job is still running, and — via
 * {@link pollUntil} — for a short grace window after a launch, so a just-created job appears even
 * though its snapshot is written a beat later by the job's async first step (the table would otherwise
 * be empty, and "poll only while a non-terminal row exists" would never start).
 */
export function useBulkSendJobs(
  page: number,
  size: number,
  filters: BulkSendJobsFilters,
  pollUntil = 0,
  autoRefresh = false,
): UseQueryResult<BulkSendJobPageViewModel> {
  const params: BulkSendJobsParams = {
    page,
    size,
    ...(filters.jobId.trim().length > 0 ? { jobId: filters.jobId.trim() } : {}),
    ...(filters.createdBy !== null && filters.createdBy.length > 0 ? { createdBy: filters.createdBy } : {}),
    ...(filters.templateId !== null && filters.templateId.length > 0 ? { templateId: filters.templateId } : {}),
    ...(filters.status !== null && filters.status.length > 0 ? { status: filters.status } : {}),
  };
  return useQuery<BulkSendJobPageViewModel>({
    queryKey: ['bulk-send', 'jobs', page, size, params.jobId ?? '', params.createdBy ?? '', params.templateId ?? '', params.status ?? ''] as const,
    queryFn: async ({ signal }) => {
      const envelope = await bulkSendEndpoints.listJobs({ ...params, signal });
      return {
        rows: envelope.content.map(mapJob),
        totalElements: envelope.totalElements ?? envelope.content.length,
        totalPages: envelope.totalPages ?? 1,
        page: envelope.number ?? page,
      };
    },
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const rows = query.state.data?.rows ?? [];
      // Snappy 4s cadence while a job is running or in the post-launch grace window...
      if (rows.some((r) => !r.isTerminal) || Date.now() < pollUntil) return JOBS_POLL_MS;
      // ...otherwise a steady 15s only when the user turned on "keep updated" (for delivery updates).
      return autoRefresh ? AUTO_REFRESH_MS : false;
    },
  });
}

/**
 * Summary of a bulk-send run (counts + problem items), for the expanded row. Pass a positive
 * {@link refetchIntervalMs} to poll it live (e.g. while "keep updated" is on).
 */
export function useBulkSendSummary(
  jobExecutionId: string | null,
  refetchIntervalMs: number | false = false,
): UseQueryResult<BulkSendSummaryViewModel> {
  return useQuery<BulkSendSummaryViewModel>({
    queryKey: ['bulk-send', 'summary', jobExecutionId] as const,
    queryFn: async ({ signal }) => {
      if (jobExecutionId === null) {
        throw new Error('missing job execution id');
      }
      return mapSummary(await bulkSendEndpoints.getSummary(jobExecutionId, { signal }));
    },
    enabled: jobExecutionId !== null,
    refetchInterval: refetchIntervalMs,
  });
}
